<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import DocumentAttachmentUploadForm from '@/components/ui/DocumentAttachmentUploadForm.vue'
import ModalHeader from '@/components/ui/ModalHeader.vue'
import ModalQrCodeCard from '@/components/ui/ModalQrCodeCard.vue'
import { attachmentFiles, useAttachmentUpload } from '@/composables/useAttachmentUpload'
import Validators from '@/utils/validators'

const props = defineProps({
  adapter: { type: Object, required: true },
  initialView: { type: String, default: 'form' },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  embedded: { type: Boolean, default: false }
})
const emit = defineEmits(['confirmed', 'cancelled', 'busyChange', 'update:snapshot'])
const { t } = useI18n()
const {
  view,
  files,
  fileName,
  metadata,
  valid,
  loading,
  loadFailed,
  sessionError,
  sessionInvalid,
  missingUpload,
  session,
  attachments,
  warnings,
  capabilities,
  localSupported,
  remaining,
  uploadBlocked,
  reloadCurrent,
  openQr,
  back,
  upload,
  confirmQr,
  cancel,
  remove
} = useAttachmentUpload(props, emit)
const headerTitle = computed(
  () =>
    props.title ||
    t(
      view.value === 'qr'
        ? 'REPAIR_FILE_ATTACHMENT.MODAL.QR_CODE_TITLE'
        : 'REPAIR_FILE_ATTACHMENT.MODAL.GENERIC_TITLE'
    )
)
const fileRules = computed(() => [
  (value) => attachmentFiles(value).length > 0 || t('ERROR_INPUT.EMPTY'),
  (value) =>
    attachmentFiles(value).length <= remaining.value ||
    t('ERROR_INPUT.MAX_FILES', { max: remaining.value }),
  ...(capabilities.value.maxBytesPerFile == null
    ? []
    : [
        (value) =>
          attachmentFiles(value).every((file) => file.size <= capabilities.value.maxBytesPerFile) ||
          t('ATTACHMENT_UPLOAD.FILE_TOO_LARGE')
      ])
])
const nameRules = computed(() => [
  ...Validators.requiredRules,
  (value) =>
    (value?.length ?? 0) <= (capabilities.value.maxFileNameLength ?? 255) ||
    t('ATTACHMENT_UPLOAD.NAME_TOO_LONG', { max: capabilities.value.maxFileNameLength ?? 255 })
])
</script>

<template>
  <section class="attachment-upload-panel" :aria-busy="loading">
    <v-progress-linear
      v-if="view === 'form' && loading"
      class="attachment-upload-panel__progress"
      indeterminate
    />
    <ModalHeader
      :title="headerTitle"
      :show-back="view === 'qr'"
      :back-disabled="loading"
      :subtitle="
        (view === 'form' && subtitle) ||
        $t(
          view === 'qr'
            ? 'REPAIR_FILE_ATTACHMENT.MODAL.QR_CODE_SUBTITLE'
            : 'REPAIR_FILE_ATTACHMENT.MODAL.SUBTITLE'
        )
      "
      @back="back"
    />
    <v-divider />
    <div v-if="warnings.length" class="modal-card__body px-4 px-sm-6 pt-4" role="status">
      <v-alert
        v-for="warning in warnings"
        :key="warning"
        class="mb-4"
        type="warning"
        variant="tonal"
        >{{ $t(warning) }}</v-alert
      >
      <v-btn
        v-if="warnings.includes('ATTACHMENT_UPLOAD.REFRESH_FAILED')"
        variant="tonal"
        :disabled="loading"
        @click="reloadCurrent"
        >{{ $t('STEPPER.RETRY') }}</v-btn
      >
    </div>
    <div v-if="loadFailed" class="modal-card__body px-4 px-sm-6 py-4">
      <v-alert type="error" variant="tonal">{{ $t('ATTACHMENT_UPLOAD.LOAD_FAILED') }}</v-alert>
      <v-btn variant="tonal" class="mt-4" :disabled="loading" @click="reloadCurrent">{{
        $t('STEPPER.RETRY')
      }}</v-btn>
      <v-btn variant="text" class="mt-4 ml-2" :disabled="loading" @click="cancel">{{
        $t('COMMON.BUTTON.CANCEL')
      }}</v-btn>
    </div>
    <template v-else-if="view === 'form'">
      <div v-if="attachments.length" class="modal-card__body px-4 px-sm-6 pt-4">
        <v-alert
          v-if="capabilities.deleteBeforeUpload"
          class="mb-4"
          type="warning"
          variant="tonal"
          >{{ $t('ATTACHMENT_UPLOAD.REMOVE_BEFORE_UPLOAD') }}</v-alert
        >
        <div
          v-for="attachment in attachments"
          :key="attachment.id"
          class="attachment-upload-panel__file mb-2"
        >
          <v-icon color="primary" icon="mdi-file-document-outline" size="20" />
          <span class="text-truncate flex-grow-1">{{ attachment.name }}</span>
          <v-btn-icon
            v-if="capabilities.supportsDelete"
            icon="mdi-close"
            :aria-label="$t('COMMON.BUTTON.REMOVE')"
            :disabled="loading"
            @click="remove(attachment)"
          />
        </div>
      </div>
      <DocumentAttachmentUploadForm
        v-if="localSupported && !(capabilities.deleteBeforeUpload && attachments.length)"
        v-model="files"
        v-model:file-name="fileName"
        v-model:valid="valid"
        :show-file-name="capabilities.requiresName === true"
        :multiple="capabilities.multiple === true"
        :accept="capabilities.accept ?? ''"
        :max-file-name-length="capabilities.maxFileNameLength ?? 255"
        :accepted-formats-label="
          $t(
            capabilities.acceptedFormatsKey ||
              (capabilities.accept
                ? 'REPAIR_FILE_ATTACHMENT.MODAL.ACCEPTED_FORMATS'
                : 'ATTACHMENT_UPLOAD.ANY_FORMAT')
          )
        "
        :file-label="$t('REPAIR_FILE_ATTACHMENT.MODAL.FILE')"
        :file-name-label="$t('REPAIR_FILE_ATTACHMENT.MODAL.FILE_NAME')"
        :file-name-placeholder="$t('REPAIR_FILE_ATTACHMENT.PLACEHOLDER.FILE_NAME')"
        :file-name-rules="nameRules"
        :file-rules="fileRules"
        :loading="loading"
        :upload-disabled="uploadBlocked"
        :show-mobile-upload="capabilities.supportsQr === true"
        :mobile-upload-label="$t('ATTACHMENT_UPLOAD.VIA_MOBILE')"
        @submit="upload"
        @cancel="cancel"
        @open-mobile-upload="openQr()"
      >
        <template #before-fields>
          <v-alert v-if="uploadBlocked" class="mb-4" type="info" variant="tonal">{{
            $t('ATTACHMENT_UPLOAD.CAPACITY_REACHED')
          }}</v-alert>
        </template>
        <template #additional-fields
          ><slot name="additional-fields" :metadata="metadata" :loading="loading"
        /></template>
      </DocumentAttachmentUploadForm>
      <div v-else class="modal-card__footer">
        <v-btn variant="tonal" :disabled="loading" @click="cancel">{{
          $t('COMMON.BUTTON.CANCEL')
        }}</v-btn>
      </div>
    </template>
    <template v-else>
      <div class="modal-card__body px-4 px-sm-6 pb-6 pt-4">
        <template v-if="sessionError">
          <v-alert type="error" variant="tonal">{{
            $t(
              sessionInvalid
                ? 'ATTACHMENT_UPLOAD.SESSION_INVALID'
                : 'FILE_STEP_GUARANTEE.QR_TOKEN_ERROR'
            )
          }}</v-alert>
          <v-btn class="mt-4" variant="tonal" :disabled="loading" @click="openQr(true)">{{
            $t('STEPPER.RETRY')
          }}</v-btn>
        </template>
        <template v-else-if="loading || session">
          <p class="text-body-2 text-medium-emphasis mb-4">
            {{ $t('FILE_STEP_GUARANTEE.QR_HELP') }}
          </p>
          <ModalQrCodeCard :value="session?.url ?? ''" :loading="loading && !session" />
          <v-alert v-if="missingUpload" class="mt-4" type="warning" variant="tonal">{{
            $t('REPAIR_FILE_ATTACHMENT.MODAL.QR_UPLOAD_MISSING')
          }}</v-alert>
        </template>
      </div>
      <v-divider />
      <div class="modal-card__footer">
        <v-btn variant="tonal" :disabled="loading" @click="cancel">{{
          $t('COMMON.BUTTON.CANCEL')
        }}</v-btn>
        <v-btn :disabled="!session || loading" :loading="loading" @click="confirmQr">{{
          $t('REPAIR_FILE_ATTACHMENT.MODAL.QR_CONFIRM')
        }}</v-btn>
      </div>
    </template>
  </section>
</template>

<style scoped>
.attachment-upload-panel {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  position: relative;
}
.attachment-upload-panel__progress {
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
}
.attachment-upload-panel :deep(.modal-header),
.attachment-upload-panel :deep(.modal-card__footer),
.attachment-upload-panel :deep(.v-divider) {
  flex-shrink: 0;
}
.attachment-upload-panel__file {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  padding: 8px 12px;
}
</style>
